import { IonRouterLink } from "@ionic/react";
import { lazy, Suspense } from "react";
import ArrowLeftIcon from "shared/components/icons/arrow/ArrowLeftIcon";
import ExternalLinkIcon from "shared/components/icons/ExternalLinkIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import PillarModalBody, {
  ModalMaxHeight,
  ModalMaxWidth,
} from "shared/components/pillar-modal/NewPillarModalBody";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { type HubDocumentPageDocumentType } from "shared/types/HubPageRouteParams";

const LazyLoadedPDFViewer = lazy(
  () => import("hub/src/components/documents/HubDocumentPDFViewer"),
);
const LazyLoadedImageViewer = lazy(
  () => import("hub/src/components/documents/HubDocumentImageViewer"),
);

type HubDocumentViewerModalProps = {
  currentDocument: HubDocumentPageDocumentType;
  control: PillarModalControl;
};

const HubDocumentViewerModal = ({
  currentDocument,
  control,
}: HubDocumentViewerModalProps) => {
  return (
    <PillarModalBody
      control={control}
      className="!p-0 m-0 overflow-hidden flex flex-col w-screen h-screen max-w-none !bg-neutral-dark/75 !rounded-none shadow-none"
      containerClasses="p-0 flex items-center justify-center w-screen h-screen max-w-none"
      dialogPanelWrapperClasses="pl-0 pr-0"
      maxW={ModalMaxWidth.Full}
      maxH={ModalMaxHeight.Full}
    >
      <div className="flex items-center justify-between px-3 py-2 bg-neutral-dark/75 text-neutral-light shadow-md z-50 shrink-0">
        <div className="flex items-center gap-x-3">
          <PillarForm.Button
            testid="document-preview-close-button"
            onClick={control.onClose}
            className="p-0.5 rounded-full bg-transparent border-none hover:bg-neutral-mid-600 transition-colors focus:outline-none"
            title="Close"
            icon={<ArrowLeftIcon className="w-4 h-4 text-neutral-light" />}
          />
          <h2
            data-testid="document-preview-title"
            className="pr-2 max-w-[40vw] text-base font-medium truncate text-neutral-light select-none"
          >
            {currentDocument.alias ?? currentDocument.title}
          </h2>
        </div>

        {currentDocument.allowOpenInBrowser && (
          <IonRouterLink
            href={currentDocument.href}
            target="_blank"
            rel="noreferrer"
            className="p-0.5 rounded-full hover:bg-neutral-mid-600 transition-colors focus:outline-none text-neutral-light"
            title="Open in New Tab"
          >
            <ExternalLinkIcon className="w-3.5 h-3.5 text-neutral-light" />
          </IonRouterLink>
        )}
      </div>

      <Suspense fallback={<div>Loading...</div>}>
        {currentDocument.href.toLowerCase().match(/\.pdf$/) ? (
          <LazyLoadedPDFViewer currentDocument={currentDocument} />
        ) : (
          <LazyLoadedImageViewer currentDocument={currentDocument} />
        )}
      </Suspense>
    </PillarModalBody>
  );
};

export default HubDocumentViewerModal;
